<script setup lang="ts">
/**
 * Inline interactive building block for the `render_text_input` tool: a free-text
 * answer collected in-chat. Emits the entered text; the dispatcher wraps it into
 * the tool's result shape (`text`).
 */
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const props = withDefaults(
  defineProps<{
    input: Record<string, unknown>;
    state: string;
    disabled?: boolean;
  }>(),
  { disabled: false },
);

const emit = defineEmits<{ (e: "submit", value: string): void }>();

const { t } = useI18n({ useScope: "local" });

const title = computed(() => (typeof props.input.title === "string" ? props.input.title : ""));
const placeholder = computed(() =>
  typeof props.input.placeholder === "string" ? props.input.placeholder : t("placeholder"),
);
const multiline = computed(() => props.input.multiline === true);

const resolved = computed(
  () =>
    props.disabled ||
    props.state === "output-available" ||
    props.state === "approval-responded" ||
    props.state === "output-error" ||
    props.state === "output-denied",
);

const text = ref<string>("");
const submitted = ref(false);
const readOnly = computed(() => resolved.value || submitted.value);

function submit() {
  const value = text.value.trim();
  if (readOnly.value || !value) {
    return;
  }
  submitted.value = true;
  emit("submit", value);
}
</script>

<template>
  <div class="not-prose space-y-3 rounded-xl border border-border bg-card p-4" data-slot="chat-block-text">
    <p v-if="title" class="text-sm font-medium text-foreground">{{ title }}</p>

    <Textarea
      v-if="multiline"
      v-model="text"
      :placeholder="placeholder"
      :disabled="readOnly"
      rows="3"
    />
    <Input
      v-else
      v-model="text"
      :placeholder="placeholder"
      :disabled="readOnly"
      @keydown.enter.prevent="submit"
    />

    <div v-if="!resolved" class="flex justify-end">
      <Button type="button" size="sm" :disabled="!text.trim() || submitted" @click="submit">
        {{ t("submit") }}
      </Button>
    </div>
  </div>
</template>

<i18n lang="yaml">
en:
  submit: Submit
  placeholder: Type your answer…
</i18n>
